import React, { createContext, useContext, useEffect, useState } from "react";

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    // Check local storage first
    const savedTheme = localStorage.getItem("theme");
    if (savedTheme) {
      return savedTheme;
    }

    // Check system preference
    if (
      window.matchMedia &&
      window.matchMedia("(prefers-color-scheme: dark)").matches
    ) {
      return "dark";
    }

    return "light";
  });

  const [systemTheme, setSystemThemeState] = useState("light");

  useEffect(() => {
    // Listen for system theme changes
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

    const handleChange = (e) => {
      setSystemThemeState(e.matches ? "dark" : "light");
    };

    // Use addEventListener instead of deprecated addListener
    mediaQuery.addEventListener("change", handleChange);
    setSystemThemeState(mediaQuery.matches ? "dark" : "light");

    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  useEffect(() => {
    // Apply theme to document
    const root = document.documentElement;
    const body = document.body;

    // Remove all theme classes
    body.classList.remove("light-mode", "dark-mode");

    // Add current theme class
    body.classList.add(`${theme}-mode`);

    // Set CSS custom properties for theme colors
    if (theme === "dark") {
      root.style.setProperty("--bg-primary", "#000");
      root.style.setProperty("--bg-secondary", "#2d3748");
      root.style.setProperty("--text-primary", "#ffffff");
      root.style.setProperty("--text-secondary", "#a0aec0");
      root.style.setProperty("--border-color", "#4a5568");
      root.style.setProperty("--shadow", "0 4px 6px rgba(0, 0, 0, 0.3)");
    } else {
      root.style.setProperty("--bg-primary", "#ffffff");
      root.style.setProperty("--bg-secondary", "#f7fafc");
      root.style.setProperty("--text-primary", "#1a202c");
      root.style.setProperty("--text-secondary", "#718096");
      root.style.setProperty("--border-color", "#e2e8f0");
      root.style.setProperty("--shadow", "0 4px 6px rgba(0, 0, 0, 0.1)");
    }

    // Save to localStorage
    localStorage.setItem("theme", theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme((prevTheme) => (prevTheme === "light" ? "dark" : "light"));
  };

  const setLightTheme = () => setTheme("light");
  const setDarkTheme = () => setTheme("dark");

  // Renamed function to avoid conflict with state setter
  const useSystemTheme = () => setTheme(systemTheme);

  const value = {
    theme,
    systemTheme,
    toggleTheme,
    setLightTheme,
    setDarkTheme,
    useSystemTheme, // renamed from setSystemTheme
    isDark: theme === "dark",
    isLight: theme === "light",
  };

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
};
